import { z } from "zod";
import { nanoid } from "nanoid";
import { isString } from "lodash";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import { checkAgentStreamSentinel } from "@/lib/agent";
import { ChatMessage, ChatReasoning, ChatReasoningSchema } from "@/schema/chat";
import { ChunkPayload } from "@/types/common";
import { SubscriptionApiFactory } from "@/apis/subscription";
import { useSubscriptionStore } from "@/store/subscription";

interface UseStreamingResponseProps {
  messages?: ChatMessage[] | null;
  onStreamError?: (chat: ChatMessage[]) => void;
  onStreamArtifact?: (reasoning: ChatReasoning) => void;
  onStreamSuccess?: (chat: ChatMessage[]) => void;
  onJSONChunk?: (state: ChatMessage[], id: string, json: ChunkPayload) => ChatMessage[];
  onStreamInterrupt?: (id: string, content: ChatReasoning) => void;
}

export function useStreamingResponse({ messages, onStreamSuccess, onStreamError, onStreamArtifact, onStreamInterrupt, onJSONChunk }: UseStreamingResponseProps) {
  const [streaming, setStreaming] = useState(false);
  const [chat, setChat] = useState<ChatMessage[]>(() => messages || []);

  const chat$ = useRef<ChatMessage[]>(messages || []);
  const setSubscription = useSubscriptionStore((state) => state.setSubscription);

  const handleCleanupStreamState = useCallback((state: ChatMessage[], id: string) => {
    return state.map((message) => {
      if (message.id === id) {
        let content = message.message;

        if (!content.length) {
          const last = message.reasoning ? message.reasoning[message.reasoning.length - 1] : null;
          if (!last || last.type !== "response.interrupt") {
            content = [{ id: nanoid(), content: "No response was received from the model. Please try your prompt again.", streaming: false, created_at: new Date().toISOString() }];
          }
        }

        return {
          ...message,
          streaming: false,
          message: content,
        };
      }
      return message;
    });
  }, []);

  const handleStreamErrorState = useCallback((state: ChatMessage[], id: string) => {
    return state.map((message) => {
      if (message.id === id) {
        return {
          ...message,
          streaming: false,
          message: "There was an error processing your request. Please try your prompt again.",
        };
      }
      return message;
    });
  }, []);

  const handleInterruptState = useCallback((state: ChatMessage[], id: string, content: any) => {
    return state.map((message) => {
      const reasoning = message.reasoning || [];

      if (message.id === id) {
        return {
          ...message,
          reasoning: [
            ...reasoning,
            {
              ...content,
              created_at: new Date().toISOString(),
            },
          ],
        };
      }
      return message;
    });
  }, []);

  const handleReasoningChunkState = useCallback((state: ChatMessage[], id: string, content: ChatReasoning) => {
    return state.map((message) => {
      const exists = message.reasoning ? message.reasoning.find((reasoning) => reasoning.id === content.id && reasoning.type === "response.end") : null;
      const reasoning = message.reasoning || [];

      if (message.id === id) {
        return {
          ...message,
          reasoning: exists
            ? reasoning.map((chunk) => {
                if (chunk.id !== content.id || chunk.type !== "response.end") {
                  return chunk;
                }
                return {
                  ...content,
                  created_at: new Date().toISOString(),
                };
              })
            : [
                ...reasoning,
                {
                  ...content,
                  created_at: new Date().toISOString(),
                },
              ],
        };
      }
      return message;
    });
  }, []);

  const handleStreamingReasoningChunkState = useCallback((state: ChatMessage[], id: string, content: ChatReasoning) => {
    return state.map((message) => {
      if (message.id === id) {
        const exists = message.reasoning ? message.reasoning.find((reasoning) => reasoning.id === content.id && reasoning.type === "response.end") : null;
        const reasoning = message.reasoning || [];

        return {
          ...message,
          reasoning: exists
            ? reasoning.map((chunk) => {
                if (chunk.id !== content.id || chunk.type !== "response.end") {
                  return chunk;
                }
                return {
                  ...chunk,
                  created_at: new Date().toISOString(),
                  content: chunk.content + content.content,
                };
              })
            : [
                ...reasoning,
                {
                  ...content,
                  created_at: new Date().toISOString(),
                },
              ],
        };
      }
      return message;
    });
  }, []);

  const handleTextChunkState = useCallback((state: ChatMessage[], id: string, runid: string, content: string | null, artifact: any | null) => {
    return state.map((message) => {
      const refined = content + "\n";

      if (message.id === id) {
        if (isString(message.message)) {
          return {
            ...message,
            artifact: artifact,
            message: message.message + refined,
          };
        }

        return {
          ...message,
          artifact: artifact,
          message: message.message.find((entry) => entry.id === runid)
            ? message.message.map((entry) =>
                entry.id === runid
                  ? {
                      id: runid,
                      content: refined,
                      streaming: false,
                      created_at: new Date().toISOString(),
                    }
                  : entry
              )
            : [
                ...message.message,
                {
                  id: runid,
                  content: refined,
                  created_at: new Date().toISOString(),
                  streaming: false,
                },
              ],
        };
      }

      return message;
    });
  }, []);

  const handleStreamingTextChunkState = useCallback((state: ChatMessage[], id: string, runid: string, content: string) => {
    return state.map((message) => {
      if (message.id === id) {
        if (isString(message.message)) {
          return {
            ...message,
            message: message.message + content,
          };
        }

        return {
          ...message,
          message: message.message.find((entry) => entry.id === runid)
            ? message.message.map((entry) =>
                entry.id === runid
                  ? {
                      ...entry,
                      content: entry.content + content,
                      created_at: new Date().toISOString(),
                    }
                  : entry
              )
            : [
                ...message.message,
                {
                  id: runid,
                  content: content,
                  created_at: new Date().toISOString(),
                },
              ],
        };
      }

      return message;
    });
  }, []);

  const handleStreamCleanup = useCallback(
    (id: string) => {
      setStreaming(false);
      setChat((state) => handleCleanupStreamState(state, id));
      chat$.current = handleCleanupStreamState(chat$.current, id);
      SubscriptionApiFactory.Apis.FetchUserSubscription().then((subscription) => setSubscription(subscription.data));
    },
    [handleCleanupStreamState, setSubscription]
  );

  const checkNetworkError = useCallback((error: unknown): boolean => {
    if (error instanceof TypeError) {
      const message = error.message ? error.message.toLowerCase() : "";
      return message.includes("network") || message.includes("failed to fetch") || message.includes("networkerror") || message.includes("network request failed") || message.includes("load failed");
    }
    if (error instanceof Error) {
      const message = error.message ? error.message.toLowerCase() : "";
      return message.includes("network") || message.includes("failed to fetch") || message.includes("networkerror") || message.includes("network request failed") || message.includes("load failed");
    }
    return false;
  }, []);

  const handleJSONChunk = useCallback(
    (json: ChunkPayload, id: string) => {
      switch (json.category) {
        case "reasoning": {
          const parsed = ChatReasoningSchema.omit({ id: true, type: true }).safeParse(json.content);
          const toolid = z.string().safeParse(json.id);

          if (!parsed.success || !toolid.success) {
            console.log("Error parsing reasoning chunk:", parsed.error);
            return;
          }

          const content: ChatReasoning = Object.assign({ id: toolid.data, type: json.type, streaming: false }, parsed.data);
          onStreamArtifact?.(content);

          setChat((state) => handleReasoningChunkState(state, id, content));
          chat$.current = handleReasoningChunkState(chat$.current, id, content);
          break;
        }

        case "reasoning.chunk": {
          const parsed = ChatReasoningSchema.omit({ id: true, type: true }).safeParse(json.content);
          const toolid = z.string().safeParse(json.id);

          if (!parsed.success || !toolid.success) {
            console.log("Error parsing reasoning chunk:", parsed.error);
            return;
          }

          const content: ChatReasoning = Object.assign({ id: toolid.data, type: json.type, streaming: true }, parsed.data);
          onStreamArtifact?.(content);

          setChat((state) => handleStreamingReasoningChunkState(state, id, content));
          chat$.current = handleStreamingReasoningChunkState(chat$.current, id, content);
          break;
        }

        case "text": {
          const runid = z.string().safeParse(json.id);
          const content = z.string().safeParse(json.content);

          if (!runid.success || !content.success) {
            console.log("Error parsing text chunk:", json);
            return;
          }

          if (!content.data) {
            console.log("Text chunk is empty:", json);
            return;
          }

          setChat((state) => handleTextChunkState(state, id, runid.data, content.data, null));
          chat$.current = handleTextChunkState(chat$.current, id, runid.data, content.data, null);
          break;
        }

        case "text.chunk": {
          const content = z.string().safeParse(json.content);
          const runid = z.string().safeParse(json.id);

          if (!content.success || !runid.success) {
            console.log("Error parsing text streaming chunk:", json);
            return;
          }

          setChat((state) => handleStreamingTextChunkState(state, id, runid.data, content.data));
          chat$.current = handleStreamingTextChunkState(chat$.current, id, runid.data, content.data);
          break;
        }

        case "interrupt": {
          const parsed = ChatReasoningSchema.omit({ id: true, type: true }).safeParse(json.content);
          const toolid = z.string().safeParse(json.id);

          if (!parsed.success || !toolid.success) {
            console.log("Error parsing interrupt chunk:", json);
            return;
          }

          const content: ChatReasoning = Object.assign({ id: toolid.data, type: json.type }, parsed.data);
          onStreamInterrupt?.(id, content);

          setChat((state) => handleInterruptState(state, id, content));
          chat$.current = handleInterruptState(chat$.current, id, content);
          break;
        }

        default: {
          if (onJSONChunk) {
            setChat((state) => onJSONChunk(state, id, json));
            chat$.current = onJSONChunk(chat$.current, id, json);
          }
          break;
        }
      }
    },
    [handleTextChunkState, handleReasoningChunkState, handleStreamingTextChunkState, handleStreamingReasoningChunkState, handleInterruptState, onStreamInterrupt, onStreamArtifact, onJSONChunk]
  );

  const handleStreamSuccess = useCallback(
    async (body: ReadableStream<Uint8Array<ArrayBufferLike>>, id: string, controller: AbortController) => {
      let counter = 0;
      let buffer = "";

      const reader = body.getReader();
      const decoder = new TextDecoder();
      const listener$ = new AbortController();

      window.addEventListener(
        "beforeunload",
        () => {
          controller.abort();
          reader.cancel();
        },
        {
          once: true,
          signal: listener$.signal,
        }
      );

      while (!controller.signal.aborted) {
        try {
          const result = await reader.read();

          if (result.done) {
            console.log("Stream completed, cleaning up!");
            handleStreamCleanup(id);
            break;
          }

          buffer += decoder.decode(result.value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const raw of lines) {
            const line = raw.trim();
            const content = line.replace(/^data:\s*/, "");

            if (controller.signal.aborted) break;
            if (!line.startsWith("data:")) continue;

            try {
              if (checkAgentStreamSentinel(content)) {
                console.log("Received sentinel, aborting stream!");
                continue;
              }

              const json: ChunkPayload = JSON.parse(content);
              handleJSONChunk(json, id);
            } catch (error) {
              console.log("=== error parsing payload ===");
              console.log({ error, line });
            }
          }
        } catch (error) {
          console.log("=== error decoding response ===");
          console.log(error);
          counter += 1;

          if (checkNetworkError(error)) {
            toast.warning("Network connectivity issue", {
              duration: Infinity,
              description: navigator.onLine ? "Ran into an error while connecting to the server." : "Please check your internet connection and try again.",
              action: {
                label: "Refresh",
                onClick: () => {
                  window.location.reload();
                },
              },
            });
            console.log("Network error detected, aborting stream!");
            handleStreamCleanup(id);
            break;
          }

          if (counter > 100) {
            console.log("Too many errors, aborting stream!");
            handleStreamCleanup(id);
            break;
          }
        }
      }

      listener$.abort();
      onStreamSuccess?.(chat$.current);
    },
    [handleJSONChunk, handleStreamCleanup, onStreamSuccess, checkNetworkError]
  );

  const handleStreamError = useCallback(
    (id: string, error?: unknown) => {
      onStreamError?.(chat$.current);
      setStreaming(false);

      setChat((state) => handleStreamErrorState(state, id));
      chat$.current = handleStreamErrorState(chat$.current, id);
      console.log("Stream Error:", error);
    },
    [handleStreamErrorState, onStreamError]
  );

  return [
    { chat, streaming, chat$ },
    { handleStreamSuccess, handleStreamError, setChat, setStreaming },
  ] as const;
}
